/**
 * The account dialog the shell's own menu opens (Figma `67:847`): who is
 * signed in, the interface language, the theme, and a way out.
 *
 * Two panes, not the frame's four. `Avisos` and `Sessões` are drawn as rail
 * entries with no pane behind them and nothing in this estate produces a
 * notification preference or a session list, so they are absent rather than
 * inert -- a control that stores a preference nothing reads tells the reader
 * something untrue. The same reading drops the frame's timezone select, its
 * avatar upload and its two notification switches. `Idioma` and `Tema` both
 * survive because both are already read on every render, by `getLocale()`
 * and by `tokens.css` respectively.
 *
 * The language is reported to the caller rather than applied here: every
 * `getLocale()` reader samples the value while it renders, so the choice only
 * reaches the screen once the whole tree is rebuilt, and that decision is the
 * plane's, not this dialog's.
 */

import { useState, type ReactNode } from 'react';
import { Check, Image, LogOut, Users, X } from 'lucide-react';

import { getLocale, type Locale } from '../../i18n';
import { IconHint } from '../hint';
import { Avatar, AvatarFallback } from '../../ui/avatar';
import { Button } from '../../ui/button';
import { Dialog, DialogClose, DialogContent, DialogTitle } from '../../ui/dialog';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { initials } from './initials';
import { sh } from './messages';
import { applyThemeChoice, isThemeChoice, readThemeChoice } from './themeChoice';

/** How the caller signed in, as the two methods the platform accepts. */
export type AccountProvider = 'google' | 'password';

type Pane = 'account' | 'appearance';

function isLocale(value: string): value is Locale {
  return value === 'pt-BR' || value === 'en';
}

/** A label beside the control it names, the frame's row for every setting. */
function SettingRow(props: { id: string; label: string; children: ReactNode }): ReactNode {
  return (
    <div className="flex items-center justify-between gap-4 border-b py-4 last:border-b-0">
      <Label htmlFor={props.id} className="text-body-sm font-normal">
        {props.label}
      </Label>
      {props.children}
    </div>
  );
}

function Identity(props: {
  name: string | null;
  email: string | null;
  provider: AccountProvider | null;
}): ReactNode {
  const heading = props.name ?? props.email;
  if (heading === null) return null;

  const method =
    props.provider === null
      ? null
      : props.provider === 'google'
        ? sh('settings.provider.google')
        : sh('settings.provider.password');
  // The email heads the row whenever no display name was ever captured, so
  // repeating it underneath would state one fact twice.
  const parts = [props.email === heading ? null : props.email, method].filter(
    (part): part is string => part !== null,
  );

  return (
    <div className="flex items-center gap-4 border-b py-4">
      <Avatar size="lg">
        <AvatarFallback data-testid="account-initials" aria-hidden>
          {initials(heading)}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0">
        <p className="text-title-sm truncate">{heading}</p>
        {parts.length > 0 && (
          <p className="text-body-sm text-muted-foreground truncate">{parts.join(' · ')}</p>
        )}
      </div>
    </div>
  );
}

export function AccountSettings(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  name: string | null;
  email: string | null;
  provider: AccountProvider | null;
  onLocaleChange: (locale: Locale) => void;
  onSignOut: () => void;
}): ReactNode {
  const [pane, setPane] = useState<Pane>('account');
  const [theme, setTheme] = useState(readThemeChoice);

  function chooseTheme(next: string): void {
    if (!isThemeChoice(next)) return;
    setTheme(next);
    applyThemeChoice(next);
  }

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent
        showCloseButton={false}
        size="xl"
        // The primitive's own close button carries an untranslated name.
        aria-describedby={undefined}
        // `p-0!`: the rail is painted to the dialog's own edge, and `cn`'s
        // class table does not know `p-xl` is padding, so it keeps both and
        // lets stylesheet order decide which one lands.
        className="overflow-hidden p-0! sm:min-h-96"
      >
        <Tabs
          orientation="vertical"
          value={pane}
          onValueChange={(next) => setPane(next as Pane)}
          className="min-h-0 flex-1 gap-0 max-sm:flex-col"
        >
          <div className="bg-muted/40 flex flex-col gap-3 border-b p-3 sm:w-52 sm:border-r sm:border-b-0">
            <DialogTitle className="text-caption text-muted-foreground px-2 pt-2 uppercase">
              {sh('settings.title')}
            </DialogTitle>
            {/* No line-style variant: that variant's own tabs-list rules
             *  force every trigger's active background transparent
             *  regardless of the default variant's own active-state
             *  background, which is the filled card the rail wants.
             *  `bg-transparent` here is the other half -- it cancels the
             *  default variant's own opaque `bg-muted`, which would
             *  otherwise paint one plate behind the whole list instead of
             *  one card per entry. */}
            <TabsList className="w-full flex-col items-stretch gap-1 bg-transparent p-0">
              <TabsTrigger value="account">
                <Users aria-hidden />
                {sh('settings.tab.account')}
              </TabsTrigger>
              <TabsTrigger value="appearance">
                <Image aria-hidden />
                {sh('settings.tab.appearance')}
              </TabsTrigger>
            </TabsList>
            <Button
              type="button"
              variant="ghost"
              className="mt-auto justify-start"
              onClick={() => props.onSignOut()}
            >
              <LogOut aria-hidden />
              {sh('settings.signOut')}
            </Button>
          </div>

          <div className="flex min-w-0 flex-1 flex-col overflow-y-auto">
            <header className="flex items-center justify-between gap-4 px-6 py-4">
              <h3 className="text-title-sm">
                {pane === 'account' ? sh('settings.tab.account') : sh('settings.tab.appearance')}
              </h3>
              <IconHint label={sh('settings.close')}>
                <DialogClose asChild>
                  <Button type="button" variant="ghost" size="icon-sm">
                    <X aria-hidden />
                  </Button>
                </DialogClose>
              </IconHint>
            </header>

            <TabsContent value="account" className="px-6">
              <Identity name={props.name} email={props.email} provider={props.provider} />
              <SettingRow id="settings-language" label={sh('settings.language')}>
                <Select
                  value={getLocale()}
                  onValueChange={(next) => {
                    if (isLocale(next)) props.onLocaleChange(next);
                  }}
                >
                  <SelectTrigger id="settings-language" className="w-48">
                    <SelectValue placeholder={sh('settings.language')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pt-BR">{sh('settings.language.ptBR')}</SelectItem>
                    <SelectItem value="en">{sh('settings.language.en')}</SelectItem>
                  </SelectContent>
                </Select>
              </SettingRow>
              <p className="text-body-sm text-muted-foreground mt-4 flex items-center gap-1.5">
                <Check aria-hidden className="size-4" />
                {sh('settings.autosave')}
              </p>
            </TabsContent>

            <TabsContent value="appearance" className="px-6">
              <SettingRow id="settings-theme" label={sh('theme.label')}>
                <Select value={theme} onValueChange={chooseTheme}>
                  <SelectTrigger id="settings-theme" className="w-48">
                    <SelectValue placeholder={sh('theme.label')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="light">{sh('theme.light')}</SelectItem>
                    <SelectItem value="dark">{sh('theme.dark')}</SelectItem>
                    <SelectItem value="system">{sh('theme.system')}</SelectItem>
                  </SelectContent>
                </Select>
              </SettingRow>
            </TabsContent>
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
